// frontend/src/components/formularios/campos/CampoModal.jsx
import React, { useState } from 'react';
import {
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Stack,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  FullscreenExit as FullscreenExitIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  TextFields as TextFieldsIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';

const CampoModal = ({
  value = '',
  onChange,
  label = 'Campo de texto',
  placeholder = 'Escriba aquí...',
  disabled = false,
  required = false,
  maxLength = null,
  minHeight = 300,
  previewLines = 2,
  showStats = false,
  helperText = null,
  sx = {}
}) => {
  const [open, setOpen] = useState(false);
  const [tempValue, setTempValue] = useState('');
  
  // Abrir modal
  const handleOpen = () => {
    if (disabled) return;
    setTempValue(value || '');
    setOpen(true);
  };
  
  // Guardar cambios
  const handleSave = () => {
    onChange(tempValue);
    setOpen(false);
  };
  
  // Cancelar cambios
  const handleCancel = () => {
    setTempValue(value || '');
    setOpen(false);
  };
  
  // Calcular estadísticas
  const getStats = (text) => {
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    const chars = text.length;
    const lines = text.split('\n').length;
    return { words, chars, lines };
  };
  
  // Crear preview del texto
  const getPreview = () => {
    if (!value || value.trim() === '') {
      return {
        text: placeholder,
        isEmpty: true
      };
    }
    
    const lines = value.split('\n');
    const preview = lines.slice(0, previewLines).join('\n');
    const hasMore = lines.length > previewLines || preview.length > 100;
    
    return {
      text: hasMore ? preview.substring(0, 100) + '...' : preview,
      isEmpty: false,
      hasMore
    };
  };
  
  const preview = getPreview();
  const stats = getStats(value || '');
  const tempStats = getStats(tempValue);
  
  return (
    <>
      {/* Campo compacto con preview */}
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          cursor: disabled ? 'default' : 'pointer',
          transition: 'all 0.2s ease',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': disabled ? {} : {
            bgcolor: 'action.hover',
            borderColor: 'primary.main'
          },
          ...sx
        }}
        onClick={handleOpen}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1} mr={1}>
            <Typography
              variant="body2"
              color={preview.isEmpty ? 'text.secondary' : 'text.primary'}
              sx={{
                fontStyle: preview.isEmpty ? 'italic' : 'normal',
                lineHeight: 1.4,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {preview.text}
            </Typography>
            
            {showStats && !preview.isEmpty && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                <Chip
                  label={`${stats.words} palabras`}
                  size="small"
                  variant="outlined"
                  color={stats.words > 50 ? 'warning' : 'default'}
                />
                <Chip
                  label={`${stats.chars} caracteres`}
                  size="small"
                  variant="outlined"
                  color={stats.chars > 300 ? 'error' : 'default'}
                />
              </Stack>
            )}
          </Box>
          
          <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
            {!disabled && (
              <Tooltip title={`Editar ${label.toLowerCase()}`}>
                <IconButton size="small" color="primary">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            
            {preview.hasMore && (
              <Chip
                label="..."
                size="small"
                color="info"
                variant="outlined"
                sx={{ minWidth: 'auto', height: 20 }}
              />
            )}
          </Box>
        </Box>
      </Paper>
      
      {/* Modal de edición */}
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '60vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <TextFieldsIcon color="primary" />
              <Typography variant="h6">
                {label}
                {required && <Typography component="span" color="error"> *</Typography>}
              </Typography>
            </Box>
            
            <IconButton onClick={handleCancel} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ p: 3 }}>
          <TextField
            fullWidth
            multiline
            value={tempValue}
            onChange={(e) => {
              if (maxLength && e.target.value.length > maxLength) return;
              setTempValue(e.target.value);
            }}
            placeholder={placeholder}
            variant="outlined"
            rows={Math.max(10, Math.ceil(minHeight / 24))}
            sx={{
              '& .MuiInputBase-root': {
                minHeight: minHeight,
                alignItems: 'flex-start'
              },
              '& textarea': {
                resize: 'vertical',
                minHeight: minHeight - 32 // Ajustar por padding
              }
            }}
            inputProps={{
              maxLength: maxLength
            }}
          />
          
          {helperText && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {helperText}
            </Typography>
          )}
          
          {/* Estadísticas del editor */}
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Stack direction="row" spacing={1}>
                <Chip
                  icon={<AutoAwesomeIcon />}
                  label={`${tempStats.words} palabras`}
                  size="small"
                  color={tempStats.words > 100 ? 'warning' : 'primary'}
                  variant="outlined"
                />
                <Chip
                  label={`${tempStats.chars} caracteres`}
                  size="small"
                  color={tempStats.chars > 500 ? 'error' : 'default'}
                  variant="outlined"
                />
                <Chip
                  label={`${tempStats.lines} líneas`}
                  size="small"
                  variant="outlined"
                />
              </Stack>
              
              {maxLength && (
                <Typography variant="caption" color="text.secondary">
                  {tempValue.length}/{maxLength}
                </Typography>
              )}
            </Stack>
            
            {/* Indicadores de calidad */}
            <Box sx={{ mt: 1 }}>
              {tempStats.words < 5 && tempValue.trim() && (
                <Chip
                  label="Muy corto - Considera agregar más detalles"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
              )}
              
              {tempStats.words > 150 && (
                <Chip
                  label="Texto extenso - Considera dividir en puntos"
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        
        <Divider />
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCancel}
            variant="outlined"
            color="inherit"
          >
            Cancelar
          </Button>
          
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            color="primary"
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CampoModal;